import { Mesh, ShaderMaterial, Vector2, Vector4 } from 'three';

import gsap from 'gsap';

import fragmentShader from '../../../../shaders/hero-sign-up-frag.glsl';
import vertexShader from '../../../../shaders/image-vert.glsl';

export default class Hero extends Mesh {

	scroll = 0;

	constructor({
		el,
		assets,
		sizes,
		geometry,
		renderer,
		isPreload
	}) {
		super();

		this.isAnimating = false;
		this.isPreload = isPreload;

		this.el = el;
		this.renderer = renderer;

		this.assets = assets;
		this.texture = this.assets[0].texture;
		this.texture2 = this.assets[1].texture;

		this.sizes = sizes;
		this.bounds = this.assets[0].image.bounds;

		this.geometry = geometry;

		this.createMaterial();
		this.addEventListeners();
	}

	addEventListeners() {
		this.el.addEventListener('signup:click', this.#onButtonClick);
		this.el.addEventListener('signup:back', this.#onBackClick);
	}

	removeEventListeners() {
		this.el.removeEventListener('signup:click', this.#onButtonClick);
		this.el.removeEventListener('signup:back', this.#onBackClick);
	}

	#onButtonClick = () => {
		this.zoomBlurIn();
	}

	#onBackClick = () => {
		this.zoomBlurOut();
	}

	/**
	 * Create.
	 */

	createMaterial() {
		const aspect = this.calculateAspect();

		this.material = new ShaderMaterial({
			depthTest: false,
			depthWrite: false,
			transparent: true,
			uniforms: {
				image: {
					value: this.texture
				},
				image2: {
					value: this.texture2
				},
				imageResolution: {
					type: 'v4',
					value: new Vector4(this.bounds.width, this.bounds.height, aspect.a1, aspect.a2)
				},
				resolution: {
					type: 'v2',
					value: new Vector2(this.sizes.screen.width, this.sizes.screen.height)
				},
				direction: {
					type: 'v2',
					value: new Vector2(0, 1.0)
				},
				alpha: {
					value: 0
				},
				scale: {
					value: 0
				},
				progress: {
					value: 0
				},
				progressZoom: {
					value: 0
				},
				progressBlur: {
					value: 0
				},
				parallax: {
					value: 0
				},
				smoothness: {
					value: 0.5
				},
				zoomQuickness: {
					value: 0.8
				},
				blurIntensity: {
					value: 0.1
				}
			},
			fragmentShader,
			vertexShader
		});

		this.setPosition();
	}

	calculateAspect() {
		const aspect = this.bounds.imageHeight / this.bounds.imageWidth;
		let a1;
		let a2;

		if (this.bounds.height / this.bounds.width > aspect) {
			a1 = (this.bounds.width / this.bounds.height) * aspect;
			a2 = 1;
		} else {
			a1 = 1;
			a2 = this.bounds.height / this.bounds.width / aspect;
		}

		return { a1, a2 };
	}

	setPosition(isResize = false) {
		const { top, left } = this.bounds;
		const scale = !isResize ? 1.35 : 1;

		this.scale.set(this.bounds.width * scale, this.bounds.height * scale, ((this.bounds.width / 2) * scale));
		this.position.y = this.scroll + this.sizes.screen.height / 2 - top - this.bounds.height / 2;
		this.position.x = 0 - this.sizes.screen.width / 2 + left + this.bounds.width / 2;
	}

	// update(scroll) {
		// this.scroll = scroll;
	// }

	resize({ sizes, bounds }) {
		this.sizes = sizes;
		this.bounds = bounds;

		const aspect = this.calculateAspect();

		this.material.uniforms.imageResolution.value.set(this.bounds.width, this.bounds.height, aspect.a1, aspect.a2);
		this.material.uniforms.resolution.value.set(this.sizes.screen.width, this.sizes.screen.height);

		if (!this.isAnimating) {
			this.setPosition(true);
		}
	}

	/**
	 * Animations.
	 */

	show() {
		// this.isAnimating = true;

		const ease = 'power4.inOut';

		this.timelineIn = gsap.timeline();
		this.timelineIn
			.to(this.scale, {
				ease,
				duration: 2,
				x: this.bounds.width,
				y: this.bounds.height,
				z: this.bounds.width / 2,
				delay: this.isPreload ? 0 : 0
			}, 'start')
			.to(this.material.uniforms.alpha, {
				ease,
				duration: 1,
				value: 1,
				delay: this.isPreload ? 0 : 0
			}, 'start')
			.to(this.material.uniforms.progress, {
				ease,
				duration: 4,
				value: 1,
				delay: this.isPreload ? 0 : 0
			}, 'start');
	}

	hide(onComplete, isTop) {
		this.isTop = isTop;
		return new Promise(resolve => {
			this.isAnimating = true;

			this.timelineOut = gsap.timeline({
				onComplete
			});

			this.timelineOut
				.to(this.material.uniforms.alpha, {
					ease: 'power4.inOut',
					duration: 0.5,
					value: 0
				}, 'start')
				.call(() => {
					this.isAnimating = false;
					resolve();
				});
		});
	}

	zoomBlurIn() {
		const ease = 'power4.inOut';
		this.timelineZoomBlurIn = gsap.timeline({
			onComplete: () => {
				this.timelineZoomBlurIn.kill();
				this.timelineZoomBlurIn = null;
			}
		});
		this.timelineZoomBlurIn
			.to(this.material.uniforms.progressZoom, {
				ease,
				duration: 2,
				value: 0.25
			}, 'start')
			.to(this.material.uniforms.progressBlur, {
				ease,
				duration: 2,
				value: 1
			}, 'start');
	}

	zoomBlurOut() {
		const ease = 'power4.inOut';
		this.timelineZoomBlurOut = gsap.timeline({
			onComplete: () => {
				this.timelineZoomBlurOut.kill();
				this.timelineZoomBlurOut = null;
			}
		});
		this.timelineZoomBlurOut
			.to(this.material.uniforms.progressZoom, {
				ease,
				duration: 2,
				value: 0
			}, 'start')
			.to(this.material.uniforms.progressBlur, {
				ease,
				duration: 2,
				value: 0
			}, 'start');
	}

	/**
	 * Destroy.
	 */

	destroyGeometry() {
		if (this.geometry) {
			this.geometry.dispose();
		}
	}

	destroyMaterial() {
		if (this.material) {
			this.material.dispose();
		}
	}

	destroy() {
		this.renderer.domElement.style.filter = '';
		this.timelineIn = null;
		this.timelineOut = null;
		this.destroyGeometry();
		this.destroyMaterial();
		this.removeEventListeners();
	}

}
